import { getWeekKey } from './functions';
import {
  CommercialProgram,
  Commitment,
  ProgramDateBasis,
  ProgramUnit,
} from './types';

/**
 * Lógica del Programa Comercial compartida por la página, el widget del
 * dashboard y el portal: unidades, historial y cumplimiento.
 */

export const PROGRAM_UNITS: ProgramUnit[] = ['kg', 'ceq', 'shipment'];

/** Claves de traducción (namespace commitments). */
export const PROGRAM_UNIT_LABELS: Record<ProgramUnit, string> = {
  kg: 'Kilos',
  ceq: 'Cajas equivalentes',
  shipment: 'Embarques',
};

export const PROGRAM_UNIT_SHORT: Record<ProgramUnit, string> = {
  kg: 'kg',
  ceq: 'CEQ',
  shipment: 'emb.',
};

type Equivalences = Pick<CommercialProgram, 'kg_per_ceq' | 'kg_per_shipment'>;

const kgPer = (unit: ProgramUnit, eq: Equivalences): number | null => {
  if (unit === 'kg') return 1;
  if (unit === 'ceq') return Number(eq.kg_per_ceq) || null;
  return Number(eq.kg_per_shipment) || null;
};

/** Convierte entre unidades; null si falta la equivalencia (kg por embarque). */
export const convertQuantity = (
  quantity: number,
  from: ProgramUnit,
  to: ProgramUnit,
  eq: Equivalences
): number | null => {
  if (from === to) return quantity;
  const fromKg = kgPer(from, eq);
  const toKg = kgPer(to, eq);
  if (!fromKg || !toKg) return null;
  return (quantity * fromKg) / toKg;
};

/** Se puede mostrar en esta unidad (embarques necesita kg por embarque). */
export const canShowUnit = (unit: ProgramUnit, eq: Equivalences) =>
  kgPer(unit, eq) != null;

/**
 * Compromisos vigentes a una fecha. Cada cambio deja la fila anterior con
 * deleted_at, así que una fila estaba vigente si se creó antes y no se había
 * reemplazado todavía. Sin fecha: los vigentes hoy.
 */
export const commitmentsAt = (rows: Commitment[], asOf?: string | null) =>
  rows.filter((row) => {
    if (!asOf) return !row.deleted_at;
    const created = row.created_at ? new Date(row.created_at).getTime() : 0;
    const at = new Date(asOf).getTime();
    return created <= at && (!row.deleted_at || new Date(row.deleted_at).getTime() > at);
  });

/** receiverId → semana → cantidad, en la unidad pedida. */
export type CommittedMatrix = Map<number, Map<number, number>>;

export const buildCommittedMatrix = (
  rows: Commitment[],
  program: CommercialProgram,
  unit: ProgramUnit
): CommittedMatrix => {
  const matrix: CommittedMatrix = new Map();
  for (const row of rows) {
    const value = convertQuantity(
      Number(row.quantity) || 0,
      (row.unit as ProgramUnit) || program.unit,
      unit,
      program
    );
    if (value == null || value === 0) continue;
    const byWeek = matrix.get(row.exporter_receiver_id) || new Map<number, number>();
    byWeek.set(row.week, (byWeek.get(row.week) || 0) + value);
    matrix.set(row.exporter_receiver_id, byWeek);
  }
  return matrix;
};

/** Un grupo de despachos: mismo cliente y mismas fechas. */
export interface ProgramShipment {
  exporter_receiver_id?: number | null;
  receiver?: string | null;
  ship_etd?: string | null;
  ship_eta?: string | null;
  total_kilos?: number | null;
  quantity?: number | null;
  box_weight?: number | null;
  /** Embarques reales del grupo (contenedores o aéreos). */
  shipments_count?: number | null;
}

export const weekOfDate = (date?: string | null): number | null => {
  if (!date) return null;
  const key = getWeekKey(date);
  const match = key ? String(key).match(/W(\d{1,2})$/) : null;
  return match ? parseInt(match[1], 10) : null;
};

export const shipmentWeek = (shipment: ProgramShipment, basis: ProgramDateBasis) =>
  weekOfDate(basis === 'eta' ? shipment.ship_eta : shipment.ship_etd);

export const shipmentKilos = (shipment: ProgramShipment) =>
  shipment.total_kilos != null
    ? Number(shipment.total_kilos) || 0
    : (Number(shipment.box_weight) || 0) * (Number(shipment.quantity) || 0);

/**
 * Lo despachado en la unidad del programa. En embarques se cuentan los
 * embarques reales, no kilos divididos.
 */
export const shippedAmount = (
  shipment: ProgramShipment,
  unit: ProgramUnit,
  eq: Equivalences
): number => {
  if (unit === 'shipment') return Number(shipment.shipments_count) || 0;
  return convertQuantity(shipmentKilos(shipment), 'kg', unit, eq) || 0;
};

/**
 * Lo despachado por cliente y semana, según la fecha base. Aparte, lo que no
 * tiene fecha y lo que cae fuera de las semanas de la temporada.
 */
export const buildShippedMatrix = ({
  shipments,
  receiverIdOf,
  receiverIds,
  weeks,
  unit,
  dateBasis,
  equivalences,
}: {
  shipments: ProgramShipment[];
  receiverIdOf: (shipment: ProgramShipment) => number | null;
  /** Solo estos clientes (filtro); sin filtro, todos. */
  receiverIds?: Set<number>;
  weeks: number[];
  unit: ProgramUnit;
  dateBasis: ProgramDateBasis;
  equivalences: Equivalences;
}) => {
  const matrix: CommittedMatrix = new Map();
  let withoutDate = 0;
  let outsideSeason = 0;
  for (const shipment of shipments) {
    const receiverId = receiverIdOf(shipment);
    if (!receiverId || (receiverIds && !receiverIds.has(receiverId))) continue;
    const amount = shippedAmount(shipment, unit, equivalences);
    const week = shipmentWeek(shipment, dateBasis);
    if (week == null) {
      withoutDate += amount;
      continue;
    }
    if (!weeks.includes(week)) {
      outsideSeason += amount;
      continue;
    }
    const byWeek = matrix.get(receiverId) || new Map<number, number>();
    byWeek.set(week, (byWeek.get(week) || 0) + amount);
    matrix.set(receiverId, byWeek);
  }
  return { shipped: matrix, withoutDate, outsideSeason };
};

export interface ComplianceSummary {
  committedToDate: number;
  shippedToDate: number;
  totalCommitted: number;
  totalShipped: number;
  /** Despachado vs. comprometido hasta la semana actual (null sin compromiso). */
  toDatePct: number | null;
  /** Despachado vs. el total comprometido de la temporada. */
  totalPct: number | null;
}

const sumUntil = (values: number[], cutoffIndex: number) =>
  values.slice(0, Math.max(0, Math.min(cutoffIndex + 1, values.length))).reduce((a, b) => a + b, 0);

export const computeCompliance = (
  committedByWeek: number[],
  shippedByWeek: number[],
  cutoffIndex: number
): ComplianceSummary => {
  const committedToDate = sumUntil(committedByWeek, cutoffIndex);
  const shippedToDate = sumUntil(shippedByWeek, cutoffIndex);
  const totalCommitted = committedByWeek.reduce((a, b) => a + b, 0);
  const totalShipped = shippedByWeek.reduce((a, b) => a + b, 0);
  return {
    committedToDate,
    shippedToDate,
    totalCommitted,
    totalShipped,
    toDatePct: committedToDate > 0 ? (shippedToDate / committedToDate) * 100 : null,
    totalPct: totalCommitted > 0 ? (totalShipped / totalCommitted) * 100 : null,
  };
};

/** Decimales según la unidad: embarques con decimales, kilos y cajas enteros. */
export const formatProgramQuantity = (value: number, unit: ProgramUnit) =>
  value.toLocaleString('es-CL', {
    maximumFractionDigits: unit === 'shipment' ? 2 : 0,
  });
